import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageSquarePlus, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface FeedbackPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement>;
}

export const FeedbackPopover: React.FC<FeedbackPopoverProps> = ({
  isOpen,
  onClose,
  anchorRef,
}) => {
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const popoverRef = useRef<HTMLDivElement>(null);

  // Reset form when closing
  useEffect(() => {
    if (!isOpen && submitStatus === 'idle') {
      setFeedback('');
      setEmail('');
      setName('');
    }
  }, [isOpen, submitStatus]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {onClose();}
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        // Check if click is not on the anchor button
        if (anchorRef?.current && !anchorRef.current.contains(e.target as Node)) {
          onClose();
        }
      }
    };

    if (isOpen) {
      // Small delay to prevent immediate close on open
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, anchorRef]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedback.trim()) {
      alert('Por favor, escribe tu feedback');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await axios.post(`${API_BASE_URL}/api/v1/feedback`, {
        feedback,
        email: email || 'No proporcionado',
        name: name || 'Anónimo',
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
      });

      setSubmitStatus('success');
      setTimeout(() => {
        onClose();
        setFeedback('');
        setEmail('');
        setName('');
        setSubmitStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error sending feedback:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate position based on anchor
  const getPosition = () => {
    if (!anchorRef?.current) {return {};}

    const rect = anchorRef.current.getBoundingClientRect();
    const popoverWidth = 400;
    const popoverHeight = 420;

    // Position to the left of the button
    const left = rect.left - popoverWidth - 12;
    // Center vertically with the button, but adjust if too close to edges
    let top = rect.top + (rect.height / 2) - (popoverHeight / 2);

    // Adjust if too close to top/bottom
    if (top < 20) {top = 20;}
    if (top + popoverHeight > window.innerHeight - 20) {
      top = window.innerHeight - popoverHeight - 20;
    }

    return {
      position: 'fixed' as const,
      left: `${left}px`,
      top: `${top}px`,
      zIndex: 50,
    };
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, scale: 0.95, x: 10 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 0.95, x: 10 }}
          style={getPosition()}
          className="w-96 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquarePlus className="w-5 h-5 text-gray-700" />
                <h3 className="text-lg font-semibold">Enviar Feedback</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                disabled={isSubmitting}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Tu opinión nos ayuda a mejorar Caramba!
            </p>

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="Tu nombre (opcional)"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                  placeholder="tu@email.com (opcional)"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 resize-none"
                  placeholder="¿Qué te parece la app? ¿Qué podemos mejorar?"
                  rows={4}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {submitStatus === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">¡Gracias por tu feedback!</span>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">Error al enviar. Intenta de nuevo.</span>
                </motion.div>
              )}

              <button
                type="submit"
                className="w-full px-4 py-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || submitStatus === 'success'}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Enviar Feedback
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Arrow pointing to button */}
          <div className="absolute top-1/2 -right-2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-white" />
        </motion.div>
      )}
    </AnimatePresence>
  );
};